import { useState } from 'react'
import { Box, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material'
import { MENU_PAPER_SX, MENU_ITEM_SX } from '@repo/ui'
import { CheckIcon } from 'lucide-react'

import { date } from 'utils'
import type { ActivationUsageRange } from 'services/billingApi'

import { InlineSelectButton } from 'components/input'

/**
 * Ranges the activations chart offers. "This month" is FIRST and is the default,
 * because the billing period is what the rest of the card is about; the longer
 * windows answer a different question ("is this growing?") and are opt-in.
 */
export const ACTIVATION_RANGE_OPTIONS: readonly {
  value: ActivationUsageRange
  label: string
}[] = [
  { value: 'month', label: 'This month' },
  { value: '6m', label: '6 months' },
  { value: '12m', label: '12 months' },
]

export const activationRangeLabel = (range: ActivationUsageRange): string =>
  ACTIVATION_RANGE_OPTIONS.find(option => option.value === range)?.label ??
  'This month'

/**
 * The activations chart's range picker.
 *
 * It is a dropdown rather than a segmented control, and it sits in the card's top
 * band beside the activation-trigger picker, because a selected segment is a
 * filled, brand-tinted block: three static options that are almost never changed
 * were rendering as the loudest thing in a card whose actual subject is a 32px
 * figure and a rate ladder. A dropdown states the current choice at the same
 * weight as its neighbour and costs one click to change.
 *
 * Its label is "Chart:" for a reason. The control moved AWAY from the chart, and
 * the headline count, the delta and the tier meter are billing-period quantities
 * that do not follow it — so the trigger has to name what it scopes, or its new
 * position implies it scopes the whole card.
 */
export const ActivationRangeSelect = ({
  value,
  onChange,
  availableRanges,
  companyCreatedAt,
  companyName,
}: {
  value: ActivationUsageRange
  onChange: (range: ActivationUsageRange) => void
  /**
   * Ranges the company is old enough for. UNDEFINED means "not evaluated" (an API
   * predating the field), and every range stays enabled — suppressing one on a
   * guess is worse than offering a window that turns out to be mostly empty.
   */
  availableRanges?: ActivationUsageRange[]
  companyCreatedAt?: string | null
  companyName?: string | null
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const select = (range: ActivationUsageRange) => {
    setAnchorEl(null)
    if (range !== value) onChange(range)
  }

  /**
   * Why a disabled option and not a hidden one (§46): the reason is the company's
   * AGE, which resolves on its own with time and is nobody's mistake. Hiding it
   * would mean a brand never learns the longer views exist, then finds them
   * appearing one day with no explanation. Disabled plus a reason says "this is
   * here, it is not for you yet, and here is when it becomes yours".
   *
   * §46's forcing function is absolute — a disabled item MUST carry a tooltip — so
   * the sentence is built here, beside the disable, and cannot be omitted.
   */
  const joinedLabel = companyCreatedAt ? date(companyCreatedAt, 'MMMM yyyy') : null
  const disabledReason = joinedLabel
    ? `Not enough history yet. ${companyName || 'Your company'} joined Vera in ${joinedLabel}.`
    : 'Not enough history yet for this window.'

  return (
    <>
      <InlineSelectButton
        label="Chart:"
        value={activationRangeLabel(value)}
        ariaLabel="Activations chart range"
        onClick={event => setAnchorEl(event.currentTarget)}
      />

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // Same house menu chrome as the trigger picker beside it (MENU_PAPER_SX:
        // the 8px trigger gap, the 4px paper inset, the matching list inset, the
        // viewport-safe height).
        slotProps={{
          paper: { sx: { ...MENU_PAPER_SX, width: 260, maxWidth: '92vw' } },
        }}
      >
        {ACTIVATION_RANGE_OPTIONS.map(option => {
          const isCurrent = option.value === value
          const disabled = availableRanges
            ? !availableRanges.includes(option.value)
            : false

          const row = (
            <MenuItem
              key={option.value}
              disabled={disabled}
              onClick={() => select(option.value)}
              sx={MENU_ITEM_SX}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ width: '100%' }}
              >
                <Typography
                  variant="body3"
                  fontWeight={isCurrent ? 600 : 400}
                  sx={{ flexGrow: 1 }}
                >
                  {option.label}
                </Typography>
                {/* Right-aligned selected check — same treatment as SelectField. */}
                <Box sx={{ width: 18, flexShrink: 0, display: 'flex' }}>
                  {isCurrent && <CheckIcon size={18} strokeWidth={2} />}
                </Box>
              </Stack>
            </MenuItem>
          )

          // A disabled MUI MenuItem swallows pointer events, so the tooltip needs a
          // wrapper that still receives them. `display: block` keeps the row full
          // width; without it the span shrink-wraps and the row goes ragged.
          return disabled ? (
            <Tooltip
              key={option.value}
              title={disabledReason}
              arrow
              placement="left"
            >
              <Box component="span" sx={{ display: 'block' }}>
                {row}
              </Box>
            </Tooltip>
          ) : (
            row
          )
        })}
      </Menu>
    </>
  )
}
